import { useEffect, useState, type ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { Box, Button, CircularProgress, Typography } from '@mui/material'
import { ErrorCodeEnum } from '@ulysses-inc/harami_api_client'
import {
  getRequestError,
  isAbortError,
  isNetworkError,
} from '~/adapter/api/error'
import gotonSrc from '~/assets/goton/goton-aseri.svg'
import logoSrc from '~/assets/logo/logo.svg'
import { KioskErrorContent } from '~/components/layout/KioskErrorScreen/KioskErrorScreen'
import { PageLayout } from '~/components/layout/PageLayout/PageLayout'
import { appMode } from '~/config'
import { useLogout } from '~/domain/logout/useLogout'
import {
  useAppVersionConsistency,
  selectIsAppVersionConsistent,
} from '~/stores/appVersionConsistency/appVersionConsistency'
import {
  kioskAuthErrorKindFromErrorCode,
  useKioskAuthErrorStore,
} from '~/stores/kioskAuthError/kioskAuthError'
import { useOfflineModeStore } from '~/stores/offlineMode/offlineMode'
import { replaceLocation } from '~/utils/navigation'
import { useSyncedAuthState } from '../hooks/useSyncedAuthState'
import { routes } from './paths'

type Props = {
  children: ReactNode
}

type AuthCheckResult =
  | 'passed'
  | 'failed'
  | 'failed_with_past_auth_state'
  | 'service_assignment_required'
  | 'relogin_required'
  | 'unknown'

type AuthCheckFailure = Extract<
  AuthCheckResult,
  'failed' | 'service_assignment_required' | 'relogin_required'
>

const errorCodeByFailure: Record<AuthCheckFailure, ErrorCodeEnum> = {
  service_assignment_required: ErrorCodeEnum.ServiceAssignmentRequiredError,
  relogin_required: ErrorCodeEnum.InvalidAudienceError,
  failed: ErrorCodeEnum.AuthenticationExpired,
}

const isAuthCheckFailure = (
  result: AuthCheckResult,
): result is AuthCheckFailure => result in errorCodeByFailure

/**
 * ログイン状態チェックのリクエスト (`GET /me`) が失敗した場合の判定結果を返す
 */
const resolveErroredAuthCheckResult = async (
  error: Error,
  isAuthed: boolean,
): Promise<AuthCheckResult> => {
  // ネットワーク断・リクエスト中断による失敗は認証エラーではない。
  // 認証エラー扱いでログイン画面へ送ると、端末にログイン状態が残っている場合は
  // RumpLogin がホームへ戻すため、`/` と `/login` を往復し続ける。
  // 認証状態は端末に残っている情報で判断し、画面表示後のデータ取得の失敗は
  // 各画面のエラーハンドリングに委ねる。
  if ((isNetworkError(error) || isAbortError(error)) && isAuthed) {
    return 'passed'
  }

  const reqErr = await getRequestError(error)
  if (reqErr.type === 'unexpectedError') {
    return 'failed'
  }

  switch (reqErr.code) {
    case ErrorCodeEnum.ServiceAssignmentRequiredError:
      return 'service_assignment_required'
    case ErrorCodeEnum.InvalidAudienceError:
      return 'relogin_required'
    default:
      return 'failed'
  }
}

export function ProtectedRoute({ children }: Props) {
  const isAppVersionConsistent = useAppVersionConsistency(
    selectIsAppVersionConsistent,
  )
  const { isAuthed, isSyncedNow, error } = useSyncedAuthState()
  const kioskAuthErrorDetected = useKioskAuthErrorStore(
    state => state.kioskAuthErrorDetected,
  )

  const [authCheckResult, setAuthCheckResult] =
    useState<AuthCheckResult>('unknown')
  useEffect(() => {
    const checkAuth = async () => {
      if (error) {
        setAuthCheckResult(await resolveErroredAuthCheckResult(error, isAuthed))
        return
      }

      // NOTE: その場でsyncした際はerrorが返るが、syncしなかった場合は返らないのでisAuthed:falseをエラー扱いする必要がある
      if (!isSyncedNow && !isAuthed) {
        setAuthCheckResult('failed_with_past_auth_state')
        return
      }

      if (isAuthed) {
        setAuthCheckResult('passed')
        return
      }

      setAuthCheckResult('unknown')
    }

    void checkAuth()
  }, [error, isAuthed, isSyncedNow])

  // キオスク端末（kiosk）にはログインの概念が無いため /login へは遷移せず、その場に
  // 全画面エラー（KioskAuthError）を被せる。/login へ遷移させると、証明書なし端末で
  // 「/ → /me 失敗 → /login → / …」のリロードループになる。
  // アプリのバージョン不整合時は全 API が失敗して認証判定自体ができないため、
  // エラー画面は出さず UpdateModal（GlobalLayout 常設）に任せる。
  useEffect(() => {
    if (!appMode.isKiosk) return
    if (!isAuthCheckFailure(authCheckResult)) return
    if (!isAppVersionConsistent) return
    kioskAuthErrorDetected(
      kioskAuthErrorKindFromErrorCode(errorCodeByFailure[authCheckResult]),
    )
  }, [authCheckResult, isAppVersionConsistent, kioskAuthErrorDetected])

  switch (authCheckResult) {
    case 'unknown':
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      )
    case 'failed_with_past_auth_state':
      return appMode.isKiosk ? (
        <KioskAuthErrorInOfflineMode />
      ) : (
        <AuthErrorInOfflineMode />
      )
    case 'service_assignment_required':
    case 'relogin_required':
    case 'failed': {
      if (appMode.isKiosk) {
        // 上の effect で立てたフラグにより GlobalLayout が全画面エラーを被せる
        return null
      }
      return (
        <Navigate
          to={routes.login.buildPath({
            queryParams: {
              // アプリのバージョンが不整合の場合、全てのAPIコールが基本的に失敗するため、
              // ログイン状態かどうかの判定自体ができない。そのため、認証切れのフィードバックは行わない
              errorCode: isAppVersionConsistent
                ? errorCodeByFailure[authCheckResult]
                : undefined,
            },
          })}
          replace
        />
      )
    }
    case 'passed':
      return children
  }
}

function AuthErrorInOfflineMode() {
  const { logout } = useLogout()

  return (
    <PageLayout header={{ component: null }} mainLayoutType="center">
      <Box
        sx={{
          width: '100%',
          maxWidth: '400px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box component="img" src={logoSrc} sx={{ width: '280px' }} />
        <Typography variant="h1" sx={{ mt: 6 }}>
          オフラインモード中にエラーが発生しました。
        </Typography>
        <Typography variant="body2" sx={{ mt: 2 }}>
          改めてログインをしてから、再度オフラインモードに切り替えてください。
          <br />
          問題が解決しない場合は、お手数ですが担当者にお問い合わせください。
        </Typography>
        <Box component="img" src={gotonSrc} sx={{ width: '50%', mt: 3 }} />
        <Button
          onClick={async () => {
            await logout({ userInitiated: true })
          }}
          variant="contained"
          sx={{ width: '100%', mt: 2 }}
          size="large"
        >
          ログインする
        </Button>
      </Box>
    </PageLayout>
  )
}

/**
 * キオスク端末（kiosk）でオフラインモード中に端末の認証情報が失われていた場合の画面。
 * Device には「ログインする」操作が存在しないため、Rump ガイドラインの
 * ひな形（管理者側の対応が必要 → 再試行）に合わせる。
 * 再試行はオフラインモードを解除して / から Device 認証をやり直す。
 */
function KioskAuthErrorInOfflineMode() {
  const offlineModeOff = useOfflineModeStore(state => state.offlineModeOff)

  return (
    <PageLayout header={{ component: null }} mainLayoutType="center">
      <KioskErrorContent
        title="オフラインモード中に端末の認証情報が確認できないため、ご利用いただけません"
        paragraphs={[
          '端末を管理している社内のシステム管理者までご連絡ください。',
          '設定が完了した後に、以下の「再試行」を押すとオフラインモードを解除してご利用いただけます。',
        ]}
        action={
          <Button
            onClick={() => {
              offlineModeOff()
              replaceLocation(routes.home.path)
            }}
            variant="contained"
            size="large"
          >
            再試行
          </Button>
        }
      />
    </PageLayout>
  )
}
